import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioProps,
  Switch,
  SwitchProps,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import {StopRounded} from '@material-ui/icons';
import React from 'react';

type ControlComponentProps = SwitchProps | CheckboxProps | RadioProps;
type ControlType = 'switch' | 'checkbox' | 'radio';

type QuoteSwitchFormControlProps = ControlComponentProps & {
  controlType: ControlType;
  label?: React.ReactNode;
  error?: boolean;
  helperText?: string;
};

const CustomSwitch = withStyles({
  root: {
    width: 60,
    height: 30,
    padding: 0,
    margin: '0.5rem',
  },
  switchBase: {
    padding: 1,
    top: '0.100rem',
    left: '0.125rem',
    '&$checked': {
      transform: 'translateX(1.9rem)',
      color: '#ffffff',
      '& + $track': {
        backgroundColor: '#0033A0',
        opacity: 1,
        border: 'none',
      },
    },
    '&$focusVisible $thumb': {
      color: '#0033A0',
      border: '0.375rem solid #ffffff',
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: '1.5625rem',
    opacity: 1,
    backgroundColor: '#D9D9D9',
    transition: 'background-color 0.3s ease, border 0.3s ease',
  },
  checked: {},
  focusVisible: {},
})(Switch);

const CustomCheckbox = withStyles({
  root: {
    color: '#2FA0FF',
    '&.Mui-checked': {
      color: '#004EA8',
    },
    '&.Mui-checked.Mui-disabled': {
      color: '#B7B8BE',
    },
  },
  checked: {},
})(Checkbox);

const CustomRadio = withStyles({
  root: {
    '&$checked': {
      color: '#004EA8',
    },
  },
  checked: {},
})(Radio);

const controlElementMap: Record<string, React.ElementType> = {
  switch: CustomSwitch,
  checkbox: CustomCheckbox,
  radio: CustomRadio,
};

const DisabledCheckboxFilledIcon = () => (
  <StopRounded style={{color: 'rgba(0,0,0,0.15)', width: '2.35rem', height: '2.35rem'}} />
);

const Control = (props: ControlComponentProps & {controlType: ControlType}) => {
  const {controlType, ...controlProps} = props;
  const ControlElement = controlElementMap[props.controlType];
  return controlType === 'checkbox' ? (
    <ControlElement {...controlProps} icon={controlProps.disabled ? <DisabledCheckboxFilledIcon /> : undefined} />
  ) : (
    <ControlElement {...controlProps} />
  );
};

const useFormControlStyles = makeStyles(() => ({
  formControlLabel: {
    '& > .MuiFormControlLabel-label': {
      color: '#474747',
    },
  },
}));

export const QuoteFormControlElement = (props: QuoteSwitchFormControlProps) => {
  const {label, error, helperText, hidden, ...controlProps} = props;

  const classes = useFormControlStyles();

  return (
    !hidden && (
      <FormControl fullWidth error={error} disabled={controlProps.disabled}>
        <FormControlLabel
          control={<Control {...controlProps} />}
          label={label}
          htmlFor={controlProps.id}
          className={classes.formControlLabel}
        />
        <>{helperText && <FormHelperText>{helperText}</FormHelperText>}</>
      </FormControl>
    )
  );
};
